import { useMemo, useState } from "react";

export default function ComparisonBuilder({
    metadata = [],
    value = {},
    onChange,
    errors = {},
}) {
    const autoGenerate = value.auto_generate ?? false;
    const saved = value.saved ?? false;
    const items = value.items ?? [];

    const conditions = useMemo(() => {
        return [
            ...new Set(
                metadata
                    .map((row) => row.condition?.trim())
                    .filter(Boolean)
            ),
        ];
    }, [metadata]);

    const allComparisons = useMemo(() => {
        const comparisons = [];

        for (let i = 0; i < conditions.length; i++) {
            for (let j = 0; j < conditions.length; j++) {
                if (i === j) continue;

                comparisons.push({
                    condition_a: conditions[i],
                    condition_b: conditions[j],
                });
            }
        }

        return comparisons;
    }, [conditions]);

    const [selectedTest, setSelectedTest] = useState("");
    const [selectedControl, setSelectedControl] = useState("");
    const [comparisonError, setComparisonError] = useState("");


    const handleAddComparison = () => {
        if (!selectedTest || !selectedControl) {
            setComparisonError("Please select both Test and Control.");
            return;
        }

        if (selectedTest === selectedControl) {
            setComparisonError("Test and Control cannot be the same.");
            return;
        }

        const exists = items.some(
            (item) =>
                item.test === selectedTest &&
                item.control === selectedControl
        );

        if (exists) {
            setComparisonError("This comparison has already been added.");
            return;
        }

        onChange({
            ...value,
            items: [
                ...items,
                {
                    test: selectedTest,
                    control: selectedControl,
                },
            ],
        });

        setSelectedTest("");
        setSelectedControl("");
        setComparisonError("");
    };

    const handleSaveComparisons = () => {
        if (items.length === 0) {
            setComparisonError("Please add at least one comparison.");
            return;
        }

        onChange({
            ...value,
            saved: true,
        });

        setComparisonError("");
    };

    return (
        <div>
            <h3 className="text-sm font-semibold text-gray-800">
                Comparison Builder
            </h3>

            <div className="mt-5">
                <label className="flex items-center gap-3">
                    <input
                        type="checkbox"
                        checked={autoGenerate}
                        onChange={(e) => {
                            const checked = e.target.checked;

                            if (checked) {
                                const generated = allComparisons.map((comparison) => ({
                                    test: comparison.condition_a,
                                    control: comparison.condition_b,
                                }));

                                onChange({
                                    auto_generate: true,
                                    saved: true,
                                    items: generated,
                                });
                            } else {
                                onChange({
                                    auto_generate: false,
                                    saved: false,
                                    items: [],
                                });
                            }

                            setComparisonError("");
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-[#0B6936] focus:ring-[#0B6936]"
                    />

                    <span className="text-sm font-medium text-gray-700">
                        Perform All Possible Comparisons For the Given Conditions
                    </span>
                </label>

                <p className="mt-2 text-xs text-gray-500">
                    Automatically generate all possible comparisons using the unique
                    conditions provided in the Metadata Builder.
                </p>
            </div>
            {autoGenerate && (
                <div className="mt-6">
                    <h4 className="text-sm font-semibold text-gray-800">
                        All Possible Comparisons ({allComparisons.length})
                    </h4>

                    <div className="mt-3 overflow-x-auto">
                        <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="border px-3 py-2 text-center text-xs font-semibold">
                                        Experimental Conditions
                                    </th>

                                    <th className="border px-3 py-2 text-center text-xs font-semibold">
                                        Test
                                    </th>

                                    <th className="border px-3 py-2 text-center text-xs font-semibold">
                                        Control
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {allComparisons.map((comparison, index) => (
                                    <tr key={index}>
                                        <td className="border px-3 py-2 text-center">
                                            {index + 1}
                                        </td>

                                        <td className="border px-3 py-2 text-center">
                                            {comparison.condition_a}
                                        </td>

                                        <td className="border px-3 py-2 text-center">
                                            {comparison.condition_b}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!autoGenerate && (
                <div className="mt-6">
                    <h4 className="text-sm font-semibold text-gray-800">
                        Manual Comparisons
                    </h4>

                    <div className="mt-4 grid grid-cols-[1fr_auto_1fr] gap-4 items-end">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Test
                            </label>

                            <select
                                disabled={saved}
                                value={selectedTest}
                                onChange={(e) => setSelectedTest(e.target.value)}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                            >
                                <option value="">Select Condition</option>

                                {conditions.map((condition) => (
                                    <option key={condition} value={condition}>
                                        {condition}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="pb-2 font-semibold text-gray-500">
                            VS
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Control
                            </label>

                            <select
                                disabled={saved}
                                value={selectedControl}
                                onChange={(e) => setSelectedControl(e.target.value)}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                            >
                                <option value="">Select Condition</option>

                                {conditions.map((condition) => (
                                    <option key={condition} value={condition}>
                                        {condition}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mt-5 flex items-center gap-3">
                        <button
                            type="button"
                            disabled={saved}
                            onClick={handleAddComparison}
                            className={`rounded-md px-4 py-2 text-sm font-medium text-white ${saved
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-[#0B6936] hover:bg-[#09582D]"
                                }`}
                        >
                            + Add
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                if (saved) {
                                    onChange({
                                        ...value,
                                        saved: false,
                                    });
                                    setComparisonError("");
                                } else {
                                    handleSaveComparisons();
                                }
                            }}
                            className={`rounded-md px-4 py-2 text-sm font-medium text-white ${saved
                                ? "bg-amber-600 hover:bg-amber-700"
                                : "bg-[#0B6936] hover:bg-[#09582D]"
                                }`}
                        >
                            {saved ? "Modify Comparisons" : "Save Comparisons"}
                        </button>
                    </div>

                    {comparisonError && (
                        <p className="mt-3 text-sm text-red-600">
                            {comparisonError}
                        </p>
                    )}
                    {items.length > 0 && (
                        <div className="mt-6">
                            <h4 className="text-sm font-semibold text-gray-800">
                                Added Comparisons ({items.length})
                            </h4>

                            <div className="mt-3 overflow-x-auto">
                                <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="border px-3 py-2 text-center text-xs font-semibold">
                                                #
                                            </th>

                                            <th className="border px-3 py-2 text-center text-xs font-semibold">
                                                Test
                                            </th>

                                            <th className="border px-3 py-2 text-center text-xs font-semibold">
                                                Control
                                            </th>

                                            <th className="border px-3 py-2 text-center text-xs font-semibold w-28">
                                                Action
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {items.map((item, index) => (
                                            <tr key={index}>
                                                <td className="border px-3 py-2 text-center">
                                                    {index + 1}
                                                </td>

                                                <td className="border px-3 py-2 text-center">
                                                    {item.test}
                                                </td>

                                                <td className="border px-3 py-2 text-center">
                                                    {item.control}
                                                </td>

                                                <td className="border px-3 py-2 text-center">
                                                    <button
                                                        type="button"
                                                        disabled={saved}
                                                        onClick={() =>
                                                            onChange({
                                                                ...value,
                                                                items: items.filter((_, i) => i !== index),
                                                            })
                                                        }
                                                        className={`${saved
                                                            ? "text-gray-400 cursor-not-allowed"
                                                            : "text-red-600 hover:underline"
                                                            }`}
                                                    >
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}